import { useState, useEffect } from 'react';
import api from '../../services/api';
import './PasswordResetModal.css';

interface Member {
  id: string;
  name: string | null;
  email: string;
}

interface PasswordResetModalProps {
  member: Member;
  onClose: () => void;
  onSuccess: () => void;
}

const PasswordResetModal = ({ member, onClose, onSuccess }: PasswordResetModalProps) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
    general?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (password || confirmPassword) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const validatePassword = (value: string): string | undefined => {
    if (value.length < 8) {
      return '密碼至少需要 8 個字元';
    }
    if (!/[a-z]/.test(value)) {
      return '密碼必須包含小寫字母';
    }
    if (!/[A-Z]/.test(value)) {
      return '密碼必須包含大寫字母';
    }
    if (!/[0-9]/.test(value)) {
      return '密碼必須包含數字';
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const passwordError = validatePassword(password);
    if (passwordError) {
      setErrors({ password: passwordError });
      return;
    }

    if (password !== confirmPassword) {
      setErrors({ confirmPassword: '密碼不相符' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await api.patch(`/api/admin/members/${member.id}/password`, {
        password,
        confirmPassword,
      });

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setErrors({
          general: response.error?.message || '密碼重置失敗',
        });
      }
    } catch (err) {
      setErrors({
        general: '網路錯誤，請再試一次',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = () => {
    if (!password) return null;

    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      digit: /[0-9]/.test(password),
    };

    const passed = Object.values(checks).filter(Boolean).length;
    const percentage = (passed / 4) * 100;

    return { checks, percentage };
  };

  const strength = getPasswordStrength();

  const isFormValid = () => {
    return password && confirmPassword &&
           password.length >= 8 &&
           /[a-z]/.test(password) &&
           /[A-Z]/.test(password) &&
           /[0-9]/.test(password) &&
           password === confirmPassword;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-reset-title"
      >
        <div className="modal__header">
          <h2 id="password-reset-title">重置會員密碼</h2>
          <button onClick={onClose} className="modal-close">×</button>
        </div>

        <div className="modal__body">
          <div className="member-info-summary">
            <p><strong>會員：</strong>{member.name || member.email}</p>
            <p><strong>電子郵件：</strong>{member.email}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="new-password">新密碼</label>
              <input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={errors.password ? 'input-error' : ''}
                placeholder="請輸入新密碼"
                disabled={isSubmitting}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}

              {strength && (
                <div className="password-strength">
                  <div className="password-strength-bar">
                    <div
                      className="password-strength-fill"
                      style={{ width: `${strength.percentage}%` }}
                    />
                  </div>
                  <ul className="password-requirements">
                    <li className={strength.checks.length ? 'valid' : 'invalid'}>
                      ✓ 至少 8 個字元
                    </li>
                    <li className={strength.checks.lowercase ? 'valid' : 'invalid'}>
                      ✓ 包含小寫字母
                    </li>
                    <li className={strength.checks.uppercase ? 'valid' : 'invalid'}>
                      ✓ 包含大寫字母
                    </li>
                    <li className={strength.checks.digit ? 'valid' : 'invalid'}>
                      ✓ 包含數字
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">確認密碼</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={errors.confirmPassword ? 'input-error' : ''}
                placeholder="請再次輸入新密碼"
                disabled={isSubmitting}
              />
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>

            {errors.general && (
              <div className="alert alert-error">{errors.general}</div>
            )}

            <div className="modal__footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn--secondary"
                disabled={isSubmitting}
              >
                取消
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={isSubmitting || !isFormValid()}
              >
                {isSubmitting ? '處理中...' : '重置密碼'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal;
